import { millisecondsToseconds } from "./utils";

/**
 * Fixed-size ring buffer that accumulates incoming audio samples and, once a full window has
 * arrived, hands it to the callback while only *advancing* by `hopLength` samples — the trailing
 * `windowLength - hopLength` samples are kept and reused as the leading context of the next
 * window. This overlap gives the transcription model context on both sides of a note near what
 * would otherwise be a hard window boundary. Set overlapRatio to 0 to fall back to the previous
 * non-overlapping behaviour (hopLength === windowLength).
 *
 * @param duration the duration of one window (ms)
 * @param sr sampling frequency
 * @param overlapRatio fraction (0-1, exclusive of 1) of each window that overlaps the next one
 */
export class CustomAudioBuffer {
  private ring!: Float32Array;
  private ringLength!: number; // number of valid samples currently stored, from index 0
  private windowLength!: number;
  private hopLength!: number;
  private sampleRate: number;
  private overlapRatio: number;
  private windowCount!: number;

  constructor(duration: number, sr: number, overlapRatio: number = 0) {
    this.sampleRate = sr;
    // Clamp defensively: a ratio approaching 1 would shrink the hop to ~0, making the model
    // re-analyze nearly the same window on every step (unbounded compute). This guards against a
    // future misconfiguration of the constant callers pass in, not any value seen today.
    this.overlapRatio = Math.min(0.9, Math.max(0, overlapRatio));
    this.setDuration(duration);
  }

  /** Seconds of leading context (already reported by the previous window) a window carries. */
  public get contextDurationSeconds(): number {
    return (this.windowLength - this.hopLength) / this.sampleRate;
  }

  public get hopDurationMs(): number {
    return (this.hopLength / this.sampleRate) * 1000;
  }

  public setDuration(duration: number) {
    // Guard against a pathological/misconfigured duration (<= 0, or too small relative to the
    // sample rate) producing a zero-length window — update()'s drain loop would then never make
    // progress and spin forever on the main thread.
    this.windowLength = Math.max(1, Math.floor(millisecondsToseconds(duration) * this.sampleRate));
    this.hopLength = Math.max(1, Math.round(this.windowLength * (1 - this.overlapRatio)));
    this.ring = new Float32Array(this.windowLength);
    this.ringLength = 0;
    this.windowCount = 0;
  }

  /**
     * Empty the buffer and reset the window count (the next window emitted after a clear is
     * treated as the first one, i.e. carries no discardable leading context).
     */
  public clear() {
    this.ringLength = 0;
    this.windowCount = 0;
  }

  /**
     * Append new samples; whenever enough have accumulated, invoke `callback` with a full
     * `windowLength`-sized window and slide forward by `hopLength` samples. Runs as a `while` loop
     * so a backlog (e.g. a burst of samples after a stall) is fully drained in one call instead of
     * trickling out one window per incoming call.
     * @param x input samples
     * @param callback called with the window's samples, sample rate, and how many leading seconds
     * of that window are overlap context already reported by the previous window (0 for the first
     * window since a clear/construction) — callers should discard any detection whose onset falls
     * within that leading context to avoid reporting the same note twice.
     */
  public update(x: Float32Array, callback: (data: Float32Array, sr: number, contextDurationSeconds: number) => void) {
    let offset = 0;
    while (offset < x.length) {
      const free = this.windowLength - this.ringLength;
      const toCopy = Math.min(free, x.length - offset);
      this.ring.set(x.subarray(offset, offset + toCopy), this.ringLength);
      this.ringLength += toCopy;
      offset += toCopy;

      if (this.ringLength >= this.windowLength) {
        const contextDurationSeconds = this.windowCount === 0 ? 0 : this.contextDurationSeconds;
        callback(this.ring.slice(0, this.windowLength), this.sampleRate, contextDurationSeconds);
        this.windowCount++;

        // Slide the window forward by hopLength, keeping the trailing overlap as the new head.
        this.ring.copyWithin(0, this.hopLength, this.windowLength);
        this.ringLength = this.windowLength - this.hopLength;
      }
    }
  }
}
